import React from "react";
import { Box, Link, Skeleton, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import Banner from "../../../assets/images/Entity/profile-banner.png";
import NetworkController from "../../../shared/NetworkController";
import { colors } from "../../../theme/palette";
import EntityAbout from "../../../shared/Entity/EntityAbout";
import { UserInfo } from "../../../types";
import typography from "../../../theme/typography";
import { useDataFormatter } from "../../../hooks/useDataFormatter";
import { AndroidIcon, AppleIcon } from "../../../assets/icons/MUI/EntitiesIcons";

import Groups from "./Groups";

interface CustomTabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

interface EntityTabsProps {
  tabValue: number;
  handleChangeTabValue: (event: React.SyntheticEvent, newValue: number) => void;
  entityInfo?: UserInfo;
  isEntityInfoLoading: boolean;
  isOwnProfile: boolean;
}

const a11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`,
});

const CustomTabPanel: React.FC<CustomTabPanelProps> = ({ children, value, index, ...other }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Box display="flex" flexDirection="column" gap="1rem">
        {children}
      </Box>
    )}
  </Box>
);

const EntityTabs = ({
  tabValue,
  handleChangeTabValue,
  entityInfo,
  isEntityInfoLoading,
  isOwnProfile,
}: EntityTabsProps) => {
  const { t } = useTranslation();

  const { about, entityLevel, role, countryName } = useDataFormatter(entityInfo);

  return (
    <>
      {isEntityInfoLoading ? (
        <LoadingTabs />
      ) : (
        <Box
          sx={{
            display: "flex",
            padding: "12px 24px 0",
            backgroundColor: colors.White,
            border: "1px solid #EAECF0",
            borderRadius: "8px",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleChangeTabValue}
            aria-label="entity tabs"
            sx={{
              minHeight: "unset",
              "& .MuiTabs-flexContainer": {
                gap: "16px",
              },
              "& .MuiTabs-indicator": {
                height: "3px",
              },
            }}
          >
            <Tab
              label={t("pages.entity.home")}
              {...a11yProps(0)}
              sx={{
                padding: "0 0 8px",
                minHeight: "unset",
                minWidth: "unset",
                ...typography.body14Regular,
                textTransform: "capitalize",
                fontWeight: 600,
              }}
            />
            <Tab
              label={t("pages.entity.network")}
              {...a11yProps(1)}
              sx={{
                padding: "0 0 8px",
                minHeight: "unset",
                minWidth: "unset",
                ...typography.body14Regular,
                textTransform: "capitalize",
                fontWeight: 600,
              }}
            />
            {isOwnProfile && (
              <Tab
                label={t("pages.entity.groups.groups")}
                {...a11yProps(2)}
                sx={{
                  padding: "0 0 8px",
                  minHeight: "unset",
                  minWidth: "unset",
                  ...typography.body14Regular,
                  textTransform: "capitalize",
                  fontWeight: 600,
                }}
              />
            )}
          </Tabs>
        </Box>
      )}

      <CustomTabPanel value={tabValue} index={0}>
        <EntityAbout text={about} isLoading={isEntityInfoLoading} />
        {isEntityInfoLoading ? (
          <Box
            sx={{
              backgroundColor: colors.White,
              border: "1px solid #EAECF0",
              borderRadius: "8px",
              overflow: "hidden",
              padding: "20px",
              display: "flex",
              gap: "12px",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {/* Title Skeleton */}
            <Skeleton variant="text" width="30%" height={24} sx={{ fontWeight: 700 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                columnGap: "30px",
              }}
            >
              {/* Entity Level Skeleton */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="text" width={60} height={20} />
              </Box>

              {/* Role Skeleton */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="text" width={60} height={20} />
              </Box>

              {/* Country Name Skeleton */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="text" width={60} height={20} />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: colors.White,
              border: "1px solid #EAECF0",
              borderRadius: "8px",
              overflow: "hidden",
              padding: "20px",
              display: "flex",
              gap: "12px",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Typography variant="body16Bold" sx={{ fontWeight: 700 }}>
              {t("pages.entity.basicInfo")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                columnGap: "30px",
              }}
            >
              {entityLevel && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <Typography
                    variant="body14SemiBold"
                    sx={{
                      fontWeight: 600,
                      color: colors.Black100,
                      whiteSpace: "nowrap",
                      lineHeight: "16px",
                    }}
                  >
                    {t("pages.entity.entityLevel")}
                  </Typography>
                  <Typography variant="body13Regular" sx={{ color: colors.DarkMode50 }}>
                    {entityLevel}
                  </Typography>
                </Box>
              )}
              {role && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <Typography
                    variant="body14SemiBold"
                    sx={{
                      fontWeight: 600,
                      color: colors.Black100,
                      whiteSpace: "nowrap",
                      lineHeight: "16px",
                    }}
                  >
                    {t("pages.entity.type")}
                  </Typography>
                  <Typography variant="body13Regular" sx={{ color: colors.DarkMode50 }}>
                    {role}
                  </Typography>
                </Box>
              )}
              {countryName && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <Typography
                    variant="body14SemiBold"
                    sx={{
                      fontWeight: 600,
                      color: colors.Black100,
                      whiteSpace: "nowrap",
                      lineHeight: "16px",
                    }}
                  >
                    {t("pages.entity.country")}
                  </Typography>
                  <Typography variant="body13Regular" sx={{ color: colors.DarkMode50 }}>
                    {countryName}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundImage: `url(${Banner})`,
            // backgroundPositionX: "-90px",
            padding: "25px 32px",
          }}
        >
          <Typography
            sx={{
              color: "var(--White, #FFF)",
              fontFamily: "HeadingNowVarTrial",
              fontSize: "26px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "normal",
              maxWidth: "275px",
              textTransform: "uppercase",
            }}
          >
            {t("pages.entity.professionalNetwork")}
          </Typography>
          <Typography
            sx={{
              color: "var(--White, #FFF)",
              fontFamily: "Inter",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
              maxWidth: "245px",
              display: "block",
              marginBottom: "16px",
            }}
          >
            {t("pages.entity.connectWith")}
          </Typography>
          <Box sx={{ display: "flex", columnGap: "12px" }}>
            <Link
              sx={{
                height: "40px",
                width: "135px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "50px",
                background: colors.White,
                cursor: "pointer",
              }}
              href="https://apps.apple.com/eg/app/goalngo/id6443919098"
              target="_blank"
              rel="noopener noreferrer"
            >
              <AppleIcon />
            </Link>
            <Link
              sx={{
                height: "40px",
                width: "135px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "50px",
                background: colors.White,
                cursor: "pointer",
              }}
              href="https://play.google.com/store/apps/details?id=com.goalngo_tech.goalngo&hl=en&pli=1"
              target="_blank"
              rel="noopener noreferrer"
            >
              <AndroidIcon />
            </Link>
          </Box>
        </Box>
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={1}>
        <Box
          sx={{
            padding: "23px 16px",
            background: colors.White,
            borderRadius: "8px",
            border: "1px solid #EAECF0",
          }}
        >
          <NetworkController isOwnNetworkTab={isOwnProfile} isEntity={true} />
        </Box>
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={2}>
        <Groups />
      </CustomTabPanel>
    </>
  );
};

export default EntityTabs;

export const LoadingTabs = () => {
  return (
    <Box
      sx={{
        display: "flex",
        padding: "12px 24px 0",
        backgroundColor: colors.White,
        border: "1px solid #EAECF0",
        borderRadius: "8px",
      }}
    >
      <Tabs
        aria-label="entity tabs loading skeleton"
        sx={{
          minHeight: "unset",
          "& .MuiTabs-flexContainer": {
            gap: "16px",
          },
          "& .MuiTabs-indicator": {
            height: "3px",
          },
        }}
      >
        <Box sx={{ padding: "0 0 8px", minHeight: "unset", minWidth: "unset" }}>
          <Skeleton variant="text" width={50} height={30} />
        </Box>
        <Box sx={{ padding: "0 0 8px", minHeight: "unset", minWidth: "unset" }}>
          <Skeleton variant="text" width={60} height={30} />
        </Box>
        <Box sx={{ padding: "0 0 8px", minHeight: "unset", minWidth: "unset" }}>
          <Skeleton variant="text" width={70} height={30} />
        </Box>
      </Tabs>
    </Box>
  );
};
